// src/pages/ProductDetail.jsx
import { useParams, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import FormulaireSejour from './FormulaireSejour';
import PriceCalendar from './PriceCalendar';
import GalerieProduit from './GalerieProduit';
import PageLoader from 'b2b/components/PageLoader';

/* ----------------------------- Helpers ----------------------------- */

function formatDateFR(dateStr) {
  if (!dateStr) return 'Non disponible';

  const date = new Date(dateStr);
  if (isNaN(date)) return 'Non disponible';

  return date.toLocaleDateString('fr-FR');
}

function normalizeProduct(p = {}) {
  const bestPrice = p.bestPrice ?? p.BestPrice ?? {};
  const media = Array.isArray(p.media) ? p.media : p.Media || [];
  const froms = p.froms ?? p.Froms ?? [];
  const services = p.services ?? p.info?.services ?? p.info?.[0]?.services ?? [];
  return { ...p, bestPrice, media, froms, services };
}

function dedupeServices(raw = []) {
  const seen = new Set();
  const out = [];
  const norm = (s = '') =>
    s
      .toString()
      .trim()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

  for (const s of raw) {
    const iconFile = (s?.icon || '').split('/').pop()?.toLowerCase();
    const titleNorm = norm(s?.title || '');
    const key = iconFile ? `i:${iconFile}` : `t:${titleNorm}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

const repairMojibake = (input = '') => {
  if (!input || typeof input !== 'string') return '';

  try {
    // Cas classique : UTF-8 mal lu en latin1
    return decodeURIComponent(escape(input));
  } catch (e) {
    // fallback si la chaîne contient déjà des caractères cassés partiels
    return input
      .replace(/Ã©/g, 'é')
      .replace(/Ã¨/g, 'è')
      .replace(/Ãª/g, 'ê')
      .replace(/Ã«/g, 'ë')
      .replace(/Ã /g, 'à')
      .replace(/Ã¢/g, 'â')
      .replace(/Ã®/g, 'î')
      .replace(/Ã¯/g, 'ï')
      .replace(/Ã´/g, 'ô')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã¹/g, 'ù')
      .replace(/Ã»/g, 'û')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã§/g, 'ç')
      .replace(/Å“/g, 'œ')
      .replace(/Å’/g, 'Œ')
      .replace(/â€™/g, "'")
      .replace(/â€œ/g, '"')
      .replace(/â€\x9d/g, '"')
      .replace(/â€“/g, '–')
      .replace(/â€”/g, '—')
      .replace(/â€¦/g, '…')
      .replace(/Â/g, '');
  }
};

const fixDescriptionHtml = (html = '') => {
  if (!html || typeof html !== 'string') return '';
  return fixMojibake(html);
};

const FALLBACK_IMG = '/assets/img/placeholder.jpg';

function getMediaUrls(media = []) {
  return media.map((m) => (typeof m === 'string' ? m : m?.url)).filter(Boolean);
}

/* --------------------------- Composant page --------------------------- */

function ProductDetail() {
  const { id } = useParams();
  const location = useLocation();
  const state = location.state || {};

  const [produit, setProduit] = useState(
    state?.product ? normalizeProduct(state.product) : null,
  );
  const [loading, setLoading] = useState(!state?.product);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(0);

  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (state?.product) return;
    const controller = new AbortController();
    let finished = false;

    const finish = () => {
      if (!finished) {
        setPending((p) => Math.max(0, p - 1));
        finished = true;
      }
    };

    setPending((p) => p + 1);

    async function fetchProduit() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/api/products/${id}`, {
          signal: controller.signal,
        });
        setProduit(normalizeProduct(res.data));
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        console.error('Erreur chargement produit :', err);
        setError('Impossible de charger le produit.');
      } finally {
        finish();
        setLoading(false);
      }
    }

    fetchProduit();
    return () => controller.abort();
  }, [id, state?.product]);

  const productCode = produit?._$id || produit?.id || produit?.code?.Value || null;

  const priceHuman = produit?.bestPrice?.bestPrice?.Price?._$Value
    ? `${Math.round(produit.bestPrice.bestPrice.Price._$Value / 100)} €`
    : 'Non disponible';

  const bestFrom = produit?.bestPrice?.bestPrice?._$From ?? produit?.bestPrice?._$From;

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

  const normFrom = (x) =>
    String(x ?? '')
      .toUpperCase()
      .replace(/^_?LT_/, '');

  const froms = produit?.froms ?? [];
  const matchedFrom = froms.find((f) => normFrom(f?.id) === normFrom(bestFrom));

  const services = useMemo(() => dedupeServices(produit?.services || []), [produit]);

  const imageUrls = useMemo(() => getMediaUrls(produit?.media || []), [produit]);

  const mainImage = imageUrls[0] || FALLBACK_IMG;
  const secondImage = imageUrls[1] || FALLBACK_IMG;
  const thirdImage = imageUrls[2] || FALLBACK_IMG;

  useEffect(() => {
    if (produit) {
      console.log('DETAIL produit =', produit);
      console.log('DETAIL services =', services);
    }
  }, [produit, services]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  let initialForm = state?.formData || null;
  let bookingId = state?.bookingId || null;

  if ((!initialForm || !bookingId) && id) {
    try {
      const raw = sessionStorage.getItem(`bookingCtx:${id}`);
      if (raw) {
        const ctx = JSON.parse(raw);
        initialForm = initialForm || ctx?.formData || null;
        bookingId = bookingId || ctx?.bookingId || null;
      }
    } catch {}
  }

  async function handleBookingSubmit() {
    return null;
  }

  const openImage = (src) => {
    if (!src) return;
    setSelectedImage(src);
  };

  const closeImage = () => {
    setSelectedImage(null);
  };

  if (loading)
    return (
      <p className="text-center p-4">
        {<PageLoader show={pending > 0} text="Chargement du produit..." />}
      </p>
    );

  if (error) return <p className="text-center text-danger">{error}</p>;
  if (!produit) return <p className="text-center text-danger">Produit introuvable</p>;

  const repairHtmlText = (html = '') => {
    if (!html || typeof html !== 'string') return '';
    return repairMojibake(html);
  };

  return (
    <div className="container my-4">
      {<PageLoader show={pending > 0} text="Chargement du produit..." />}

      <div className="container-fluid" style={{ marginTop: '3rem' }}>
        <div className="row g-1">
          <div className="col-12">
            <h2 className="text-secondary p-3">{produit?.description?.name}</h2>
            <h5>
              <i className="fas fa-map-marker-alt" /> {produit?.location?.city?.title} ,{' '}
              {produit?.location?.country?.title}
            </h5>
          </div>

          <div className="col-md-7">
            <img
              src={mainImage}
              alt="main"
              className="img-fluid rounded w-100 h-100"
              style={{ objectFit: 'cover', maxHeight: '516px', cursor: 'pointer' }}
              onClick={() => openImage(mainImage)}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>

          <div className="col-md-4 d-flex flex-column gap-1">
            <img
              src={secondImage}
              alt="second"
              className="img-fluid rounded w-100"
              style={{ objectFit: 'cover', height: '49%', cursor: 'pointer' }}
              onClick={() => openImage(secondImage)}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
            <img
              src={thirdImage}
              alt="third"
              className="img-fluid rounded w-100"
              style={{
                objectFit: 'cover',
                height: '49%',
                marginTop: '1rem',
                cursor: 'pointer',
              }}
              onClick={() => openImage(thirdImage)}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>

          <GalerieProduit produit={produit} />
        </div>
      </div>

      <div className="row mt-5">
        <div className="col-md-7">
          <div className="d-flex align-items-baseline">
            À partir de{' '}
            <strong>
              <h4 className="text-primary px-1">
                {priceHuman}
                <span>/pers</span>
              </h4>
            </strong>
          </div>
          <div className="d-flex">
            Ville de départ :{' '}
            <h4 style={{ color: '#000064', marginLeft: 5 }}>
              {matchedFrom ? capitalize(matchedFrom.title) : bestFrom || '—'}
            </h4>
          </div>
          <div className="d-flex">
            Le{' '}
            <h4 style={{ color: '#000064', marginLeft: 5 }}>
              {formatDateFR(produit?.bestPrice?.startsAt || produit?.bestPrice?.startAt)}
            </h4>
          </div>
          <div className="d-flex">
            Pour{' '}
            <h4 style={{ color: '#000064', marginLeft: 5 }}>
              {produit?.information?.Night ?? produit?.info?.Night ?? '—'} nuits
            </h4>
          </div>

          {(services?.length ?? 0) > 0 && (
            <div className="mt-5">
              <h2 className="h5 mb-3" style={{ color: '#f56371' }}>
                Services
              </h2>
              <div className="d-flex flex-wrap gap-3">
                {services.map((s, idx) => (
                  <div
                    key={s.id || s._id || s.title || idx}
                    className="d-flex align-items-center gap-2 border rounded px-2 py-1 col-3 justify-content-center m-2"
                  >
                    {s.icon ? (
                      <img
                        src={s.icon.startsWith('/') ? s.icon : s.icon}
                        alt=""
                        width="28"
                        height="28"
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    ) : null}
                    <span className="small pl-1">{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="col-md-5">
          <FormulaireSejour
            produit={produit}
            onSubmit={handleBookingSubmit}
            submitting={submitting}
            initialValues={initialForm}
            bookingId={bookingId}
            priceHuman={priceHuman}
          />
        </div>
      </div>

      {produit?.description?.body?.length > 0 && (
        <div className="mt-5">
          <h2 className="h5 mb-3" style={{ color: '#f56371' }}>
            Détails du produit
          </h2>

          {produit.description.body.map((section, index) => (
            <div key={index} className="bg-light p-3 rounded mb-3">
              <h3 className="h6 mb-2">{repairMojibake(section.title || '')}</h3>

              {section.paragraph?.Text ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: repairHtmlText(section.paragraph.Text),
                  }}
                />
              ) : (
                <p className="text-muted mb-0">Pas de contenu.</p>
              )}
            </div>
          ))}
        </div>
      )}

      <PriceCalendar productCode={productCode} />

      {selectedImage && (
        <div
          onClick={closeImage}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '95vh',
            }}
          >
            <button
              type="button"
              onClick={closeImage}
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                border: 'none',
                background: '#fff',
                color: '#000',
                fontSize: '24px',
                cursor: 'pointer',
                lineHeight: 1,
                zIndex: 2,
              }}
            >
              ×
            </button>

            <img
              src={selectedImage}
              alt="zoom"
              style={{
                maxWidth: '95vw',
                maxHeight: '95vh',
                objectFit: 'contain',
                borderRadius: '12px',
                display: 'block',
              }}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetail;
